class GarantiesManager {
  constructor(data) {
    this.guarantees = data.guarantees || [];
    this.rules = data.rules || {
      mandatory: [],
      default: [],
      dependencies: {},
      impacts: [],
    };
    this.initialized = false;
    this.impactRules = data.rules?.impacts || [];

    this.slugToIdMap = {};
    this.idToSlugMap = {};
    this.guarantees.forEach((g) => {
      if (g.slug) {
        this.slugToIdMap[g.slug] = g.id;
        this.idToSlugMap[g.id] = g.slug;
      }
    });

    this.rules.dependencies = this.convertDependenciesToIds(this.rules.dependencies);
  }

  convertDependenciesToIds(dependencies) {
    const converted = {};
    for (const [slug, dep] of Object.entries(dependencies)) {
      const id = this.slugToIdMap[slug];
      if (id === undefined) continue;

      converted[id] = {
        implies: (dep.implies || [])
          .map((s) => this.slugToIdMap[s])
          .filter((id) => id !== undefined),
        excludes: (dep.excludes || [])
          .map((s) => this.slugToIdMap[s])
          .filter((id) => id !== undefined),
        disables: (dep.disables || [])
          .map((s) => this.slugToIdMap[s])
          .filter((id) => id !== undefined),
      };
    }
    return converted;
  }

  initialize() {
    const mandatoryIds = (this.rules.mandatory || [])
      .map((slug) => this.slugToIdMap[slug])
      .filter((id) => id !== undefined);
    const defaultIds = (this.rules.default || [])
      .map((slug) => this.slugToIdMap[slug])
      .filter((id) => id !== undefined);

    const initialSelection = [...new Set([...defaultIds, ...mandatoryIds])];

    this.guarantees = this.guarantees.map((guarantee) => {
      const impacted = this.getImpactedAmount(guarantee.id, initialSelection);
      return {
        ...guarantee,
        is_selected: initialSelection.includes(guarantee.id),
        is_disabled: this.isDisabled(guarantee.id, initialSelection),
        is_impacted: impacted.isImpacted,
        impacted_by: impacted.impactedBy,
        original_amount: guarantee.amount,
        amount: impacted.isImpacted ? impacted.newAmount : guarantee.amount,
      };
    });

    this.initialized = true;
    return this.guarantees;
  }

  getImpactedAmount(guaranteeId, selectedIds) {
    const guarantee = this.guarantees.find((g) => g.id === guaranteeId);
    if (!guarantee) {
      return { newAmount: 0, isImpacted: false, impactedBy: null };
    }

    let newAmount = guarantee.amount;
    let isImpacted = false;
    let impactedBy = null;

    for (const impactRule of this.impactRules) {
      const sourceId = this.slugToIdMap[impactRule.source_slug];
      
      if (!sourceId) continue;
      
      if (selectedIds.includes(sourceId)) {
        for (const impact of impactRule.impacts) {
          const targetId = this.slugToIdMap[impact.target_slug];
          if (targetId === guaranteeId) {
            newAmount = impact.new_amount;
            isImpacted = true;
            impactedBy = impactRule.source_label;
            break;
          }
        }
      }
      if (isImpacted) break;
    }

    return { newAmount, isImpacted, impactedBy };
  }

  getGuaranteeIdBySlug(slug) {
    return this.slugToIdMap[slug] || null;
  }

  getSlugById(id) {
    return this.idToSlugMap[id] || null;
  }

  toggleGuarantee(guaranteeId, currentSelected) {
    const isSelected = currentSelected.includes(guaranteeId);
    let newSelected = [...currentSelected];
    const actions = [];

    const guarantee = this.guarantees.find((g) => g.id === guaranteeId);
    if (!guarantee) {
      return { newList: newSelected, actions: [] };
    }

    const rule = this.rules.dependencies[guaranteeId] || null;

    if (isSelected) {
      if (!this.canDeselect(guaranteeId)) {
        return {
          newList: newSelected,
          actions: [{ type: "blocked", id: guaranteeId }],
        };
      }

      newSelected = newSelected.filter((id) => id !== guaranteeId);
      actions.push({ type: "removed", id: guaranteeId, reason: "deselected" });

      for (const [key, dep] of Object.entries(this.rules.dependencies)) {
        if (dep.implies && dep.implies.includes(guaranteeId)) {
          const id = parseInt(key);
          if (newSelected.includes(id)) {
            newSelected = newSelected.filter((id2) => id2 !== id);
            actions.push({
              type: "removed",
              id: id,
              reason: "implied_by_" + guaranteeId,
            });
          }
        }
      }
    } else {
      if (!newSelected.includes(guaranteeId)) {
        newSelected.push(guaranteeId);
        actions.push({ type: "added", id: guaranteeId, reason: "selected" });
      }

      if (rule && rule.implies && rule.implies.length > 0) {
        rule.implies.forEach((impliedId) => {
          if (!newSelected.includes(impliedId)) {
            newSelected.push(impliedId);
            actions.push({
              type: "added",
              id: impliedId,
              reason: "implied_by_" + guaranteeId,
            });
          }
        });
      }

      if (rule && rule.excludes && rule.excludes.length > 0) {
        rule.excludes.forEach((excludedId) => {
          if (newSelected.includes(excludedId)) {
            newSelected = newSelected.filter((id) => id !== excludedId);
            actions.push({
              type: "removed",
              id: excludedId,
              reason: "excluded_by_" + guaranteeId,
            });
          }
        });
      }

      for (const [key, dep] of Object.entries(this.rules.dependencies)) {
        if (dep.excludes && dep.excludes.includes(guaranteeId)) {
          const parentId = parseInt(key);
          if (newSelected.includes(parentId)) {
            newSelected = newSelected.filter((id) => id !== guaranteeId);
            actions.push({
              type: "removed",
              id: guaranteeId,
              reason: "excluded_by_" + parentId,
            });
          }
        }
      }
    }

    const impactedGuarantees = this.applyImpacts(newSelected);

    this.guarantees = this.guarantees.map((g) => {
      const impacted = impactedGuarantees.find((ig) => ig.id === g.id);
      if (impacted) {
        return {
          ...g,
          amount: impacted.amount,
          is_impacted: impacted.is_impacted,
          impacted_by: impacted.impacted_by,
          original_amount: g.original_amount || impacted.original_amount || g.amount,
        };
      }
      return g;
    });

    return { newList: newSelected, actions, impactedGuarantees };
  }

  applyImpacts(selectedIds) {
    const impactedGuarantees = [];

    for (const guarantee of this.guarantees) {
      const impacted = this.getImpactedAmount(guarantee.id, selectedIds);
      impactedGuarantees.push({
        ...guarantee,
        amount: impacted.isImpacted ? impacted.newAmount : guarantee.original_amount || guarantee.amount,
        is_impacted: impacted.isImpacted,
        impacted_by: impacted.impactedBy,
        original_amount: guarantee.original_amount || guarantee.amount,
      });
    }

    return impactedGuarantees;
  }

  getGuaranteeState(guaranteeId, selectedIds) {
    const guarantee = this.guarantees.find((g) => g.id === guaranteeId);
    if (!guarantee) return null;

    const isSelected = selectedIds.includes(guaranteeId);
    const isMandatory = this.isMandatory(guaranteeId);
    const isDefault = this.isDefault(guaranteeId);
    const isDisabled = this.isDisabled(guaranteeId, selectedIds);

    let isExcluded = false;
    for (const [key, dep] of Object.entries(this.rules.dependencies)) {
      if (dep.excludes && dep.excludes.includes(guaranteeId)) {
        const parentId = parseInt(key);
        if (selectedIds.includes(parentId)) {
          isExcluded = true;
          break;
        }
      }
    }

    const impacted = this.getImpactedAmount(guaranteeId, selectedIds);

    const checked = isSelected || isMandatory || isDefault;
    const disabled = isDisabled || isMandatory || isDefault || isExcluded;

    return {
      id: guarantee.id,
      name: guarantee.name,
      slug: guarantee.slug,
      amount: impacted.isImpacted ? impacted.newAmount : guarantee.original_amount || guarantee.amount,
      original_amount: guarantee.original_amount || guarantee.amount,
      is_impacted: impacted.isImpacted,
      impacted_by: impacted.impactedBy,
      isSelected: isSelected,
      isMandatory: isMandatory,
      isDefault: isDefault,
      isDisabled: isDisabled,
      isExcluded: isExcluded,
      checked: checked,
      disabled: disabled,
      canToggle: !isMandatory && !isDefault && !isDisabled && !isExcluded,
      rules: guarantee.rules,
      description: guarantee.description,
      tooltip: this.getTooltipMessage(
        guaranteeId,
        isMandatory,
        isDefault,
        isDisabled || isExcluded
      ),
    };
  }

  isMandatory(guaranteeId) {
    const mandatoryIds = (this.rules.mandatory || [])
      .map((slug) => this.slugToIdMap[slug])
      .filter((id) => id !== undefined);
    return mandatoryIds.includes(guaranteeId);
  }

  isDefault(guaranteeId) {
    const defaultIds = (this.rules.default || [])
      .map((slug) => this.slugToIdMap[slug])
      .filter((id) => id !== undefined);
    return defaultIds.includes(guaranteeId);
  }

  isDisabled(guaranteeId, selectedIds) {
    for (const [key, dependency] of Object.entries(this.rules.dependencies)) {
      if (dependency.disables && dependency.disables.includes(guaranteeId)) {
        if (selectedIds.includes(parseInt(key))) {
          return true;
        }
      }
    }
    return false;
  }

  canDeselect(guaranteeId) {
    return !this.isMandatory(guaranteeId) && !this.isDefault(guaranteeId);
  }

  getTooltipMessage(guaranteeId, isMandatory, isDefault, isDisabled) {
    if (isMandatory)
      return "Cette garantie est obligatoire pour cette catégorie";
    if (isDefault) return "Cette garantie est incluse par défaut";
    if (isDisabled)
      return "Cette garantie est désactivée en raison des sélections actuelles";

    const guarantee = this.guarantees.find((g) => g.id === guaranteeId);
    if (guarantee && guarantee.rules) {
      const rule = guarantee.rules;
      if (rule.excludes && rule.excludes.length > 0) {
        const names = rule.excludes.map((id) => {
          const g = this.guarantees.find((gg) => gg.id === id);
          return g ? g.name : id;
        });
        return `Sélectionner cette garantie retirera : ${names.join(", ")}`;
      }
      if (rule.implies && rule.implies.length > 0) {
        const names = rule.implies.map((id) => {
          const g = this.guarantees.find((gg) => gg.id === id);
          return g ? g.name : id;
        });
        return `Sélectionner cette garantie ajoutera : ${names.join(", ")}`;
      }
    }
    return "";
  }

  getAllGuaranteesState(selectedIds) {
    return this.guarantees
      .map((guarantee) => this.getGuaranteeState(guarantee.id, selectedIds))
      .filter((state) => state !== null);
  }
}

export default GarantiesManager;